import { z } from 'zod';
import {
  DELISTING_ACTIONS,
  ENGINE_STATES,
  EXECUTION_MODES,
  NOTIFICATION_LEVELS,
  SENTIMENT_ADAPTER_KEYS,
  VETO_REASONS,
} from '../enums';
import { decimalInRange, decimalStringSchema, isoTimestampSchema, uuidSchema } from './common';
import { universeConfigSchema } from './universe';

/**
 * `app_settings` is a k/v jsonb table and the source of truth for hot-reload
 * (ARCHITECTURE.md §8). Every value is `unknown` coming out of the DB and must be parsed
 * with the schema registered here before use (AI_RULES §8) — that is what makes
 * ConfigService's snapshot type-safe.
 *
 * The defaults below are exactly what `001_initial.sql` seeds; keep them in sync.
 */

export const paperWalletSettingsSchema = z.object({
  startingBalanceUsdt: decimalStringSchema,
  /** Simulated slippage applied to paper fills, in basis points. */
  slippageBps: z.number().int().min(0).max(1000),
  /** Binance spot taker fee, in percent. */
  takerFeePct: decimalStringSchema,
});
export type PaperWalletSettings = z.infer<typeof paperWalletSettingsSchema>;

export const marketDataSettingsSchema = z.object({
  klineTimeframes: z.array(z.string().regex(/^\d+[mhd]$/)).min(1),
  orderbookDepth: z.number().int().min(5).max(1000),
  orderbookIntervalMs: z.number().int().min(200).max(60_000),
  /** Upper bound on ticker events forwarded per symbol; protects the bus and the WS layer. */
  tickerThrottleMs: z.number().int().min(0).max(10_000),
});
export type MarketDataSettings = z.infer<typeof marketDataSettingsSchema>;

export const notificationSettingsSchema = z.object({
  minLevel: z.enum(NOTIFICATION_LEVELS),
  telegramEnabled: z.boolean(),
  /** Identical notifications inside this window are collapsed (AI_RULES §2 rate limiting). */
  dedupeWindowSec: z.number().int().min(0).max(3600),
});
export type NotificationSettings = z.infer<typeof notificationSettingsSchema>;

export const sentimentSettingsSchema = z.object({
  /** Aggregation window per symbol. */
  windowMs: z.number().int().min(60_000).max(24 * 60 * 60 * 1000),
  providers: z.record(
    z.enum(SENTIMENT_ADAPTER_KEYS),
    z.object({
      enabled: z.boolean(),
      weight: decimalStringSchema,
      /** LLM adapters only: model id override; each adapter has its own default. */
      model: z.string().min(1).max(100).optional(),
    }),
  ),
});
export type SentimentSettings = z.infer<typeof sentimentSettingsSchema>;

/** One RSS/Atom feed. `name` becomes the `news_items.provider` suffix: 'rss:<name>'. */
export const rssFeedSchema = z.object({
  name: z.string().regex(/^[a-z0-9_-]{1,32}$/, 'lowercase letters, digits, "_" or "-"'),
  url: z.string().url(),
  enabled: z.boolean(),
});
export type RssFeed = z.infer<typeof rssFeedSchema>;

/**
 * News ingestion (ARCHITECTURE.md §9.8). Endpoints live here, not in code, so a changed
 * URL is a dashboard edit rather than a deploy (AI_RULES §1 news exception).
 */
export const newsSettingsSchema = z.object({
  sources: z.object({
    rss: z.object({
      enabled: z.boolean(),
      pollIntervalSec: z.number().int().min(15).max(3600),
      feeds: z.array(rssFeedSchema).max(50),
    }),
    binance_announcements: z.object({
      enabled: z.boolean(),
      pollIntervalSec: z.number().int().min(10).max(3600),
      /** Article list: one call returns the latest articles of every catalog. */
      listUrl: z.string().url(),
      /** Article body; `articleCode` is appended as a query parameter. */
      detailUrl: z.string().url(),
      pageSize: z.number().int().min(1).max(50),
      /** Only these catalogs are ingested; everything else Binance publishes is ignored. */
      catalogs: z.object({
        listing: z.number().int().positive(),
        delisting: z.number().int().positive(),
      }),
    }),
  }),
  /** Items already older than this when first seen (e.g. a feed's backlog at boot) are stored, not emitted. */
  maxItemAgeSec: z.number().int().min(60).max(86_400),
  /** Same normalized title from another source inside this window = duplicate. */
  dedupWindowMin: z.number().int().min(1).max(1440),
  delistingAction: z.enum(DELISTING_ACTIONS),
  /**
   * Lower-case name → base ticker ("ethereum" → "ETH"). Never alias a name that is also a
   * common English word ("optimism", "stellar", "render") — it would fire on ordinary prose.
   */
  symbolAliases: z.record(
    z.string().regex(/^[a-z0-9 .-]{2,40}$/),
    z.string().regex(/^[A-Z0-9]{2,15}$/),
  ),
  /** Tickers that are also common words: matched only as $CASHTAG or via an alias. */
  ambiguousTickers: z.array(z.string().regex(/^[A-Z0-9]{1,15}$/)),
});
export type NewsSettings = z.infer<typeof newsSettingsSchema>;

/** Active symbol vetoes, keyed by unified symbol. Cleared only by a human. */
export const symbolVetoSchema = z.object({
  reason: z.enum(VETO_REASONS),
  newsItemId: uuidSchema,
  newsTitle: z.string(),
  at: isoTimestampSchema,
});
export type SymbolVeto = z.infer<typeof symbolVetoSchema>;
export const symbolVetoesSchema = z.record(z.string(), symbolVetoSchema);
export type SymbolVetoes = z.infer<typeof symbolVetoesSchema>;

const poolPct = decimalStringSchema.refine(decimalInRange('0', '100'), 'must be between 0 and 100');
/**
 * Capital pools: the share of equity each entry strategy may have invested at once, in %.
 * A strategy without an entry is limited only by the global risk settings.
 */
export const strategyAllocationSchema = z.object({
  sentiment_first: poolPct.optional(),
  hybrid_confirmation: poolPct.optional(),
  technical_hunter: poolPct.optional(),
  trend_follower: poolPct.optional(),
});
export type StrategyAllocation = z.infer<typeof strategyAllocationSchema>;

/**
 * Per-strategy slot caps on top of the global `max_concurrent_trades`: a strategy that holds
 * positions for weeks must not starve a fast one of every slot. Missing = global cap only.
 */
export const strategyLimitSchema = z.object({ maxConcurrentTrades: z.number().int().min(1).max(50) });
export const strategyLimitsSchema = z.object({
  sentiment_first: strategyLimitSchema.optional(),
  hybrid_confirmation: strategyLimitSchema.optional(),
  technical_hunter: strategyLimitSchema.optional(),
  trend_follower: strategyLimitSchema.optional(),
});
export type StrategyLimits = z.infer<typeof strategyLimitsSchema>;

/** key → schema. Adding a key here is all it takes to make it hot-reloadable and typed. */
export const APP_SETTING_SCHEMAS = {
  execution_mode: z.enum(EXECUTION_MODES),
  engine_state: z.enum(ENGINE_STATES),
  universe: universeConfigSchema,
  paper_wallet: paperWalletSettingsSchema,
  market_data: marketDataSettingsSchema,
  notifications: notificationSettingsSchema,
  sentiment: sentimentSettingsSchema,
  news: newsSettingsSchema,
  symbol_vetoes: symbolVetoesSchema,
  allocation: strategyAllocationSchema,
  strategy_limits: strategyLimitsSchema,
} as const;

export type AppSettingKey = keyof typeof APP_SETTING_SCHEMAS;

export const APP_SETTING_KEYS = Object.keys(APP_SETTING_SCHEMAS) as readonly AppSettingKey[];

/** The fully-typed settings snapshot ConfigService keeps in memory. */
export type AppSettings = {
  readonly [K in AppSettingKey]: z.infer<(typeof APP_SETTING_SCHEMAS)[K]>;
};

/**
 * Safety defaults (AI_RULES §13): the system boots in **paper** mode, **paused** (no new
 * entries until a human resumes), with every LLM sentiment adapter disabled.
 */
export const APP_SETTING_DEFAULTS: AppSettings = {
  execution_mode: 'paper',
  engine_state: 'paused',
  universe: { topN: 30, minQuoteVolume: '50000000', refreshMinutes: 15, newListingDays: 3, listingPollSec: 60 },
  paper_wallet: {
    startingBalanceUsdt: '10000',
    slippageBps: 5,
    takerFeePct: '0.1',
  },
  market_data: {
    klineTimeframes: ['1m', '5m', '15m'],
    orderbookDepth: 20,
    orderbookIntervalMs: 2000,
    tickerThrottleMs: 250,
  },
  notifications: { minLevel: 'warn', telegramEnabled: false, dedupeWindowSec: 300 },
  sentiment: {
    windowMs: 900_000,
    providers: {
      regex: { enabled: true, weight: '1.0' },
      claude: { enabled: false, weight: '1.5' },
      openai: { enabled: false, weight: '1.5' },
    },
  },
  news: {
    sources: {
      rss: {
        enabled: true,
        pollIntervalSec: 60,
        feeds: [
          { name: 'coindesk', url: 'https://www.coindesk.com/arc/outboundfeeds/rss/', enabled: true },
          { name: 'cointelegraph', url: 'https://cointelegraph.com/rss', enabled: true },
          { name: 'decrypt', url: 'https://decrypt.co/feed', enabled: true },
          { name: 'theblock', url: 'https://www.theblock.co/rss.xml', enabled: true },
        ],
      },
      binance_announcements: {
        enabled: true,
        pollIntervalSec: 30,
        listUrl:
          'https://www.binance.com/bapi/composite/v1/public/cms/article/list/query?type=1&pageNo=1',
        detailUrl: 'https://www.binance.com/bapi/composite/v1/public/cms/article/detail/query',
        pageSize: 20,
        catalogs: { listing: 48, delisting: 161 },
      },
    },
    maxItemAgeSec: 1800,
    dedupWindowMin: 30,
    delistingAction: 'close',
    symbolAliases: {
      bitcoin: 'BTC',
      ethereum: 'ETH',
      ether: 'ETH',
      solana: 'SOL',
      ripple: 'XRP',
      cardano: 'ADA',
      dogecoin: 'DOGE',
      avalanche: 'AVAX',
      chainlink: 'LINK',
      polkadot: 'DOT',
      litecoin: 'LTC',
      tron: 'TRX',
      toncoin: 'TON',
      'shiba inu': 'SHIB',
      polygon: 'POL',
      arbitrum: 'ARB',
      'near protocol': 'NEAR',
      aptos: 'APT',
      sui: 'SUI',
      hyperliquid: 'HYPE',
      pepe: 'PEPE',
      uniswap: 'UNI',
      aave: 'AAVE',
      injective: 'INJ',
      filecoin: 'FIL',
      cosmos: 'ATOM',
      bnb: 'BNB',
    },
    ambiguousTickers: [
      'A', 'ACT', 'AI', 'ALT', 'BANK', 'DOGS', 'FUN', 'GAS', 'HIGH', 'HOOK', 'ID', 'IO',
      'MAGIC', 'MASK', 'ME', 'MOVE', 'NEAR', 'NOT', 'ONE', 'OP', 'PEOPLE', 'PORTAL', 'SUN',
      'T', 'TRUMP',
    ],
  },
  symbol_vetoes: {},
  allocation: {},
  strategy_limits: {},
};

/** GET /settings returns the whole snapshot; PUT /settings accepts any subset of keys. */
export const settingsResponseSchema = z.object(APP_SETTING_SCHEMAS);
export type SettingsResponse = z.infer<typeof settingsResponseSchema>;

/**
 * Keys with their own guarded endpoints are not writable through PUT /settings: vetoes
 * (/risk/vetoes, audit-logged), the engine state (/engine/pause|resume|panic — the state
 * machine) and the execution mode (/engine/mode — the GO-LIVE confirmation).
 */
export const settingsUpdateSchema = z
  .object(APP_SETTING_SCHEMAS)
  .omit({ symbol_vetoes: true, engine_state: true, execution_mode: true })
  .partial()
  .refine((v) => Object.keys(v).length > 0, 'at least one setting must be provided');
export type SettingsUpdate = z.infer<typeof settingsUpdateSchema>;
